import { useApolloClient, useMutation } from "@apollo/client";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PersonAdd from "@mui/icons-material/PersonAdd";
import PersonRemove from "@mui/icons-material/PersonRemove";
import {
  Alert,
  Button,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { StatusChip } from "components/StatusChip";
import {
  StaffManifestReviewManifestsQuery,
  StaffManifestReviewManifestsQueryVariables,
} from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SourcesCell } from "routes/Customer/Waste/Manifests/SourcesCell";
import { relativeLogTime } from "util/dates";

import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";
import { AssignDialog } from "../AssignDialog";
import {
  ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION,
  STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY,
  STATUS_STYLES,
  assigneeLabel,
  statusChipStyle,
  type ManifestReviewRow,
} from "./api";
import { ManifestDetailDialog } from "./ManifestDetailDialog";
import { ManifestReviewSummary } from "./ManifestReviewSummary";
import { NeedsReviewCell } from "./NeedsReviewCell";

const ASSIGNEE_PARAM = "assignee";
const ASSIGNEE_ME = "me";
const ASSIGNEE_UNASSIGNED = "unassigned";

// "Since delegated" should always read as elapsed time ("3 weeks ago"), never flip to a
// bare date. relativeLogTime falls back to an absolute date past its cutoff, so set the
// cutoff effectively to never (100 years) to keep the whole column relative.
const ALWAYS_RELATIVE_CUTOFF_HOURS = 24 * 365 * 100;

// Structured filters shown in the omnisearch popover. Keys match what the resolver
// parses (tenant / status / assignee). All column-derived text filters are excluded so
// the popover shows only these dropdown/type-ahead controls; free-text matching of
// manifest #, tenant, facility, and EPA id is handled by the omnisearch box itself.
const MANIFEST_REVIEW_FILTERS: FilterKey[] = [
  { key: "tenant", header: "Tenant", filterKeyType: "multipleTenant" },
  { key: "facility", header: "Facility", filterKeyType: "text" },
  {
    key: "status",
    header: "Status",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(STATUS_STYLES),
    enumPresentationFunction: (value) => STATUS_STYLES[value]?.label ?? value,
  },
  { key: "assignee", header: "Assignee", filterKeyType: "multipleStaffAnyUser" },
  { key: "hasDiscrepancies", header: "Has discrepancies", filterKeyType: "boolean" },
  { key: "needsReview", header: "Needs review", filterKeyType: "boolean" },
  { key: "assignedAt", header: "Since delegated", filterKeyType: "date" },
];

// Column fields whose auto-generated free-text filters we suppress (replaced by the
// structured filters above, or covered by free-text search).
const MANIFEST_REVIEW_EXCLUDED_FILTERS = [
  "manifestTrackingNumber",
  "tenantName",
  "facilityName",
  "discrepancyCount",
  "needsReview",
  "status",
  "type",
  "assignee",
  "assignedAt",
  "assignedBy",
];

// Queue + scoreboard queries to refresh after any assignment write (or failed attempt).
const ASSIGNMENT_REFETCH_QUERIES = ["StaffManifestReviewManifests", "ManifestReviewSummary"];

function viewModeFromAssignee(assignee: string | undefined): "all" | "mine" | "unassigned" {
  if (assignee === ASSIGNEE_ME) return "mine";
  if (assignee === ASSIGNEE_UNASSIGNED) return "unassigned";
  return "all";
}

export function ManifestReviewPage() {
  useBreadcrumb({ label: "Manifest Review", to: "/staff/fulfillment/waste/manifest-review" });
  const { success, error } = useAlerts();

  const [searchParams, setSearchParams] = useSearchParams();
  const parsedOmnisearch = useMemo(
    () => parseOmnisearch(searchParams.get("omnisearch") ?? ""),
    [searchParams]
  );
  const assigneeTerm: string | undefined = parsedOmnisearch[ASSIGNEE_PARAM]?.[0];
  const viewMode = viewModeFromAssignee(assigneeTerm);

  // Upsert/remove a single keyed omnisearch term while preserving free text + other terms.
  const setOmnisearchTerm = (key: string, value: string | null) => {
    const next = { ...parseOmnisearch(searchParams.get("omnisearch") ?? "") };
    if (value) {
      next[key] = [value];
    } else {
      delete next[key];
    }
    const str = stringifyOmnisearch(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (str) {
          params.set("omnisearch", str);
        } else {
          params.delete("omnisearch");
        }
        return params;
      },
      { replace: true }
    );
  };

  const [selectedRows, setSelectedRows] = useState<ManifestReviewRow[]>([]);
  // Drive the grid's selection from page state (not just mirror it): the grid caches
  // selected row objects across refetches (cross-page selection), so without this a row
  // that no longer exists after a refetch would stay silently selected and poison the
  // next bulk assignment with its stale id.
  const selectedIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  // Rows targeted by the assign picker (a single row from ＋/the modal, or the selection).
  const [assignTargets, setAssignTargets] = useState<ManifestReviewRow[]>([]);
  const [detailRow, setDetailRow] = useState<ManifestReviewRow | null>(null);

  const apolloClient = useApolloClient();
  const [assignManifests, { loading: assigning }] = useMutation(
    ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION,
    { refetchQueries: ASSIGNMENT_REFETCH_QUERIES }
  );

  const applyAssignment = useCallback(
    async (rows: ManifestReviewRow[], staffUserId: string | null) => {
      try {
        const { data } = await assignManifests({
          variables: {
            processedManifestIds: rows.map((row) => row.processedManifestId),
            staffUserId,
          },
        });
        const result = data?.assignManifestReviewManifests;
        const itemCount =
          (result?.assignedDiscrepancyCount ?? 0) + (result?.assignedDocumentReviewCount ?? 0);
        if (itemCount === 0) {
          // Every item on these manifests was resolved/reviewed since the page loaded —
          // nothing left to assign; the refetch reconciles the rows.
          success("Nothing to assign — these manifests have no active review items");
          return;
        }
        const verb = staffUserId ? "Assigned" : "Unassigned";
        success(`${verb} ${rows.length} manifest${rows.length === 1 ? "" : "s"}`);
      } catch {
        // Most likely the queue changed under the page (e.g. a manifest no longer
        // exists) — refresh it so the next attempt operates on current rows. Await the
        // refetch: if it also fails the grid may be stale, so tell the user to reload
        // rather than leaving them with a "try again" toast over un-refreshed rows.
        try {
          await apolloClient.refetchQueries({ include: ASSIGNMENT_REFETCH_QUERIES });
          error("Failed to update assignment — refreshed the queue, please try again");
        } catch {
          error(
            "Failed to update assignment and couldn't refresh the queue — please reload the page"
          );
        }
      }
    },
    [assignManifests, apolloClient, success, error]
  );

  const openAssignFor = useCallback((rows: ManifestReviewRow[]) => {
    if (rows.length === 0) return;
    setAssignTargets(rows);
    setAssignDialogOpen(true);
  }, []);

  const handleAssign = async (staffUserId: string) => {
    await applyAssignment(assignTargets, staffUserId);
    setAssignDialogOpen(false);
    setAssignTargets([]);
    setSelectedRows([]);
  };

  const rawColumns: OmnisearchGridColDef<ManifestReviewRow>[] = useMemo(
    () => [
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        width: 170,
        renderCell: ({ row }) => (
          <Link
            to={`/o/${row.tenantId}/waste/manifests/${row.processedManifestId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit" }}
            onClick={(e) => e.stopPropagation()}
          >
            {row.manifestTrackingNumber}
          </Link>
        ),
      },
      { field: "tenantName", headerName: "Tenant", flex: 1, minWidth: 140 },
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1,
        minWidth: 140,
        valueGetter: (_value, row) => row.facilityName ?? row.facilityEpaId ?? "—",
      },
      {
        field: "sources",
        headerName: "Sources",
        width: 96,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => <SourcesCell availableSources={row.availableSources} />,
      },
      {
        field: "discrepancyCount",
        headerName: "Discrepancies",
        width: 130,
        align: "left",
        headerAlign: "left",
        // Orange = unresolved, blue = pending, from the denormalized counts.
        // Document-needs-review lives in its own "Needs review" column, not here.
        renderCell: ({ row }) => {
          const unresolved = row.unresolvedDiscrepancyCount ?? 0;
          const pending = row.pendingDiscrepancyCount ?? 0;
          if (unresolved === 0 && pending === 0) return null;
          const parts: string[] = [];
          if (unresolved > 0) parts.push(`${unresolved} unresolved`);
          if (pending > 0) parts.push(`${pending} pending`);
          return (
            <Tooltip title={parts.join(" · ")} placement="top" enterDelay={400}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {unresolved > 0 && (
                  <StatusChip label={unresolved} color="warning" variant="outlined" />
                )}
                {pending > 0 && <StatusChip label={pending} color="info" variant="outlined" />}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: "needsReview",
        headerName: "Needs review",
        width: 150,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <NeedsReviewCell
            documentCount={row.documentIds.length}
            reviewPending={row.documentReviewPending}
          />
        ),
      },
      {
        field: "assignee",
        headerName: "Assignee",
        flex: 1,
        minWidth: 140,
        // Server sort is keyed by column; assignee/assignedBy resolve to a name in JS and
        // aren't server-sortable, so don't offer a no-op sort affordance on them.
        sortable: false,
        valueGetter: (_value, row) => assigneeLabel(row.assignee),
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: ({ row }) => {
          const style = statusChipStyle(row.status);
          return <StatusChip variant="outlined" color={style.color} label={style.label} />;
        },
      },
      {
        field: "assignedAt",
        headerName: "Since delegated",
        width: 150,
        valueGetter: (_value, row) =>
          row.assignedAt ? relativeLogTime(row.assignedAt, ALWAYS_RELATIVE_CUTOFF_HOURS) : "—",
      },
      {
        field: "assignedBy",
        headerName: "Delegated by",
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (_value, row) => (row.assignedBy ? assigneeLabel(row.assignedBy) : "—"),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 90,
        getActions: (params: GridRowParams<ManifestReviewRow>) =>
          params.row.assignee
            ? [
                <GridActionsCellItem
                  key="unassign"
                  icon={<PersonRemove />}
                  label="Unassign"
                  onClick={() => applyAssignment([params.row], null)}
                />,
              ]
            : [
                <GridActionsCellItem
                  key="assign"
                  icon={<PersonAdd />}
                  label="Assign"
                  onClick={() => openAssignFor([params.row])}
                />,
              ],
      },
    ],
    [applyAssignment, openAssignFor]
  );

  const filterBar = (
    <Stack
      key="manifest-review-filters"
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      sx={{ mb: 1.5 }}
    >
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        size="small"
        color="primary"
        onChange={(_e, val) => {
          if (!val) return;
          if (val === "mine") setOmnisearchTerm(ASSIGNEE_PARAM, ASSIGNEE_ME);
          else if (val === "unassigned") setOmnisearchTerm(ASSIGNEE_PARAM, ASSIGNEE_UNASSIGNED);
          else setOmnisearchTerm(ASSIGNEE_PARAM, null);
        }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="mine">Mine</ToggleButton>
        <ToggleButton value="unassigned">Unassigned</ToggleButton>
      </ToggleButtonGroup>

      <Button
        variant="contained"
        size="small"
        disabled={assigning || selectedRows.length === 0}
        onClick={() => openAssignFor(selectedRows)}
      >
        {selectedRows.length > 0 ? `Assign (${selectedRows.length})` : "Assign"}
      </Button>
      <Tooltip title="Remove assignment">
        <span>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={assigning || selectedRows.length === 0}
            startIcon={<DeleteOutline />}
            onClick={async () => {
              await applyAssignment(selectedRows, null);
              setSelectedRows([]);
            }}
          >
            Unassign
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Stack spacing={2} px={3} py={2}>
      <Alert severity="info">
        Assign staff members to review documents and field discrepancies. Open a manifest to see its
        details.
      </Alert>

      <ManifestReviewSummary />

      <Paper variant="outlined" sx={{ p: 2 }}>
        {/* All/Mine/Unassigned toggle + bulk actions, above the omnisearch bar. */}
        {filterBar}
        <OmnisearchDataGrid<
          StaffManifestReviewManifestsQuery,
          StaffManifestReviewManifestsQueryVariables,
          ManifestReviewRow
        >
          persistenceKey="manifest-review-manifests-v1"
          isURLDriven
          // Paper owns the padding; suppress the omnisearch bar's own horizontal padding.
          withPadding={false}
          // Structured filters only (Tenant type-ahead, Status dropdown, Assignee
          // type-ahead); suppress the per-column free-text filters.
          additionalFilterColumns={MANIFEST_REVIEW_FILTERS}
          excludeFilterColumns={MANIFEST_REVIEW_EXCLUDED_FILTERS}
          columns={rawColumns}
          // "Delegated by" is secondary detail — hidden by default, available via Manage Columns.
          initialColumnVisibilityModel={{ assignedBy: false }}
          dataQuery={STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY}
          getItems={(data) => data.staffManifestReviewManifests.items}
          getCount={(data) => data.staffManifestReviewManifests.totalCount}
          checkboxSelection
          disableRowSelectionOnClick
          selectedIds={selectedIds}
          onSelectedRowsChanged={setSelectedRows}
          onRowClick={(params) => setDetailRow(params.row)}
          initialPageSize={50}
          rowHeight="auto"
        />
      </Paper>

      <AssignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={handleAssign}
        title="Assign to Staff"
        selectedCount={assignTargets.length}
        isLoading={assigning}
      />

      <ManifestDetailDialog
        row={detailRow}
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        onAssign={() => {
          if (detailRow) openAssignFor([detailRow]);
          setDetailRow(null);
        }}
      />
    </Stack>
  );
}
